import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Button, Checkbox, Popover } from '@ntv360/component-pantry';

export interface FilterOption {
    label: string;
    value?: string;
    icon?: string;
    checked?: boolean;
    expanded?: boolean;
    indeterminate?: boolean;
    id?: string;
    isRadio?: boolean;
    children?: FilterOption[];
}

@Component({
    selector: 'app-filter-dropdown',
    imports: [Button, Popover, Checkbox, NgTemplateOutlet],
    templateUrl: './filter-dropdown.component.html',
    styleUrl: './filter-dropdown.component.scss',
})
export class FilterDropdownComponent {
    /**
     * View Child for the popover
     */
    protected filterByPopover = viewChild<Popover>('filterByPopover');

    /**
     * Scroll Dispatcher for scroll event
     */
    private scrollDispatcher = inject(ScrollDispatcher);

    constructor() {
        this.scrollDispatcher
            .scrolled()
            .pipe(takeUntilDestroyed())
            .subscribe(() => {
                const popover = this.filterByPopover();
                if (popover?.isVisible()) {
                    (popover as any).calculatePosition();
                }
            });
    }

    /**
     * Type of filter dropdown
     */
    public type = input<'default' | 'grouped' | 'tree'>('default');

    /**
     * Whether to show the "Filter by" label on the trigger button. Defaults to true.
     */
    public showLabel = input<boolean>(true);

    /**
     * Dropdown options input
     */
    public dropdownOptions = input<FilterOption[]>();

    /**
     * Max height (in px) of the popover before it becomes scrollable
     */
    public maxHeight = input<number>(400);

    /**
     * Grouped options input
     */
    public groupedOptions = input<FilterOption[]>();

    /**
     * Emit when an option is selected
     */
    public optionSelected = output<string | any>();

    /**
     * Emit when grouped options change
     */
    public groupedOptionsChanged = output<FilterOption[]>();

    /**
     * Emit when a tree node's checked state is toggled (tree mode)
     */
    public treeCheckedChange = output<{ node: FilterOption; checked: boolean }>();

    /**
     * Emit when a tree node's expand state is toggled (tree mode)
     */
    public treeExpandedChange = output<FilterOption>();

    /**
     * Emit when all filters are cleared
     */
    public clearAll = output<void>();

    /**
     * Ticks on every in-place option mutation so `selectedCount` recomputes.
     * Why: toggle handlers mutate `option.checked` in place, which does not
     * change the input signal reference.
     */
    private mutationTick = signal(0);

    /**
     * Number of currently selected filters. Counts leaf selections so
     * auto-checked parents in tree/grouped mode don't double-count.
     */
    protected selectedCount = computed<number>(() => {
        this.mutationTick();
        const countLeaves = (opts: FilterOption[] | undefined): number => {
            if (!opts) return 0;
            return opts.reduce((acc, opt) => {
                if (opt.children?.length) return acc + countLeaves(opt.children);
                return acc + (opt.checked ? 1 : 0);
            }, 0);
        };

        if (this.type() === 'default') return countLeaves(this.dropdownOptions());
        return countLeaves(this.groupedOptions());
    });

    /**
     * Flat list of checked leaf nodes for the chips strip.
     */
    public activeFilterChips = computed<
        { label: string; groupLabel?: string; option: FilterOption }[]
    >(() => {
        this.mutationTick();

        const collectLeaves = (
            opts: FilterOption[],
            groupLabel?: string,
        ): { label: string; groupLabel?: string; option: FilterOption }[] =>
            opts.flatMap((opt) => {
                if (opt.children?.length) return collectLeaves(opt.children, opt.label);
                return opt.checked ? [{ label: opt.label, groupLabel, option: opt }] : [];
            });

        if (this.type() === 'default') {
            return (this.dropdownOptions() || [])
                .filter((o) => o.checked)
                .map((o) => ({ label: o.label, option: o }));
        }
        return collectLeaves(this.groupedOptions() || []);
    });

    /**
     * On option click
     * @param {string} option The option that was clicked
     */
    protected onOptionClick(option: FilterOption): void {
        this.optionSelected.emit(option.label);
    }

    /**
     * Toggle group expansion
     * @param {FilterOption} group The group to toggle
     */
    protected onGroupToggle(group: FilterOption): void {
        group.expanded = !group.expanded;
        const checkedItems = this.getCheckedItems(this.groupedOptions() || []);
        this.groupedOptionsChanged.emit(checkedItems);
        this.treeExpandedChange.emit(group);
    }

    /**
     * Toggle option selection
     * @param {FilterOption} option The option to toggle
     */
    protected onOptionToggle(option: FilterOption): void {
        option.checked = !option.checked;
        this.mutationTick.update((v) => v + 1);
        const checkedItems = this.getCheckedItems(this.groupedOptions() || []);
        this.groupedOptionsChanged.emit(checkedItems);
    }

    /**
     * Handles the checked state change for a node in the tree structure.
     * Updates the node state and emits the change events.
     *
     * @param {FilterOption} node - The filter option node being toggled
     * @param {boolean} checked - The new checked state of the node
     */
    protected onTreeCheckedChange(node: FilterOption, checked: boolean): void {
        node.checked = checked;
        this.mutationTick.update((v) => v + 1);
        const checkedItems = this.getCheckedItems(this.groupedOptions() || []);
        this.groupedOptionsChanged.emit(checkedItems);
        this.treeCheckedChange.emit({ node, checked });
    }

    /**
     * Recursive node mode: toggle a node's expanded state.
     */
    protected onTreeExpandToggle(node: FilterOption, event: Event): void {
        event.stopPropagation();
        node.expanded = !node.expanded;
        const checkedItems = this.getCheckedItems(this.groupedOptions() || []);
        this.groupedOptionsChanged.emit(checkedItems);
        this.treeExpandedChange.emit(node);
    }

    /**
     * Removes a specific filter by unchecking it and updating the selection state.
     *
     * @param {FilterOption} option - The filter option to be removed
     */
    public onRemoveFilter(option: FilterOption): void {
        option.checked = false;
        this.mutationTick.update((v) => v + 1);
        const checkedItems = this.getCheckedItems(this.groupedOptions() || []);
        this.groupedOptionsChanged.emit(checkedItems);
        this.treeCheckedChange.emit({ node: option, checked: false });
    }

    /**
     * Clears all selected filters across the entire structure.
     * Resets checked and indeterminate states for all options and children.
     */
    public onClearAll(): void {
        const clearRecursive = (opts: FilterOption[]) => {
            opts.forEach((opt) => {
                opt.checked = false;
                opt.indeterminate = false;
                if (opt.children?.length) clearRecursive(opt.children);
            });
        };
        clearRecursive(
            this.type() === 'default' ? this.dropdownOptions() || [] : this.groupedOptions() || [],
        );
        this.mutationTick.update((v) => v + 1);
        this.groupedOptionsChanged.emit([]);
        this.clearAll.emit();
    }

    /**
     * Recursively traverses the filter options to find and return all items that are currently checked.
     *
     * @param {FilterOption[]} options - The array of filter options to scan
     * @returns {FilterOption[]} An array of checked filter options, including their checked descendants
     */
    private getCheckedItems(options: FilterOption[]): FilterOption[] {
        return options.flatMap((opt) => {
            // Recursively get checked items from children
            const checkedChildren = opt.children ? this.getCheckedItems(opt.children) : [];

            // Include this option if:
            // 1. It's checked itself, OR
            // 2. It has checked descendants
            if (opt.checked || checkedChildren.length > 0) {
                return [
                    {
                        ...opt,
                        children: checkedChildren.length > 0 ? checkedChildren : opt.children,
                    },
                ];
            }

            return [];
        });
    }
}
